import React, { FC, useEffect, useState } from 'react';
import { Button, Modal } from 'antd';
import { Image } from 'common/components';
import { IInventoryAssigneeShort } from 'common/models';
import { RequestDistributionCount } from 'entities/Request/components';
import { IDistributeRequestPayload, IRequestDistribution } from 'entities/Request/Request.models';
import { InventoryAssigneeSelector } from 'entities/Inventory/components';

interface IComponentProps {
  open: boolean;
  loading: boolean;
  distributions: IRequestDistribution[];
  assignees: IInventoryAssigneeShort[];
  onCancel: () => void;
  onSubmit: (payload: Omit<IDistributeRequestPayload, 'requestId'>) => void;
}

export const RequestDistributionsModal: FC<IComponentProps> = ({
  open,
  loading,
  distributions,
  assignees,
  onCancel,
  onSubmit,
}) => {
  const [distributionsData, setDistributionsData] = useState<IRequestDistribution[]>([]);

  const canSubmit = distributionsData.some((item) => item.assigneeId);

  useEffect(() => {
    setDistributionsData(
      distributions.map((item) => {
        return {
          ...item,
          assigneeId: null,
          count: item.unassignedCount,
        };
      }),
    );
  }, [open, distributions]);

  const handleCancel = (): void => {
    if (loading) {
      return;
    }

    onCancel();
  };

  const handleSubmit = (): void => {
    const items = distributionsData
      .filter((item) => item.assigneeId && item.count)
      .map((item) => ({
        inventoryId: item.inventoryId,
        assigneeId: item.assigneeId as number,
        count: item.count as number,
      }));

    onSubmit({
      items,
      onSuccess: handleCancel,
    });
  };

  const changeAssignee = (assigneeId: number | null, inventoryId: number): void => {
    setDistributionsData((prev) => {
      return prev.map((item) => {
        if (item.inventoryId === inventoryId) {
          return {
            ...item,
            assigneeId,
          };
        }

        return item;
      });
    });
  };

  const changeCount = (count: number, inventoryId: number): void => {
    setDistributionsData((prev) => {
      return prev.map((item) => {
        if (item.inventoryId === inventoryId) {
          return {
            ...item,
            count,
          };
        }

        return item;
      });
    });
  };

  return (
    <Modal
      rootClassName="redesign modal"
      width={720}
      centered
      open={open}
      onCancel={handleCancel}
      destroyOnClose
      styles={{ mask: { background: 'transparent' } }}
      footer={[
        <Button key="cancel" className="button-sm secondary" onClick={handleCancel} loading={loading}>
          Отмена
        </Button>,
        <Button key="submit" className="button-sm primary" onClick={handleSubmit} loading={loading} disabled={!canSubmit}>
          Сохранить
        </Button>,
      ]}
    >
      <div className="mb-3 ff-medium fs-m">Распределить инструмент из заказа</div>

      <div className="mb-6 ff-regular fs-xs color-dark-grey">
        Выберите сотрудника и количество инструмента, которое хотите за ним закрепить. После сохранения данные о распределении
        появятся в системе учёта.
      </div>

      <div>
        {distributionsData.map((item) => {
          return (
            <div key={item.inventoryId} className="py-4 flex align-center request-distributions-modal__item">
              <div className="mr-5">
                <Image size={48} src={item.image} />
              </div>

              <div className="mr-6 ff-regular fs-s flex-1 ellipsis-2">{item.goodName}</div>

              <div className="mr-3">
                <RequestDistributionCount
                  count={item.count as number}
                  limit={item.unassignedCount}
                  onChange={(count) => changeCount(count, item.inventoryId)}
                />
              </div>

              <InventoryAssigneeSelector
                className={`request-distributions-modal__assignee-selector ${item.assigneeId ? 'request-distributions-modal__assignee-selector-with-value' : ''}`}
                popupClassName="request-distributions-modal__assignee-selector-popup"
                allowClear
                assignees={assignees}
                value={item.assigneeId}
                onChange={(value: number) => changeAssignee(value, item.inventoryId)}
              />
            </div>
          );
        })}
      </div>
    </Modal>
  );
};
